<script setup>
import { computed, onMounted, ref } from 'vue'
import GameCenterGame from './GameCenterGame.vue';

const { mode, tournamentId, schedule, showFilters, showSelects, showModeSwitch, limit } = defineProps({
    mode: {
        type: String,
        default: 'group',
    },
    tournamentId: {
        type: Number,
        default: null,
    },
    schedule: {
        type: Boolean,
        default: true,
    },
    showFilters: {
        type: Boolean,
        default: true,
    },
    showSelects: {
        type: Boolean,
        default: true,
    },
    showModeSwitch: {
        type: Boolean,
        default: true,
    },
    limit: {
        type: Number,
        default: null,
    },
})
const currentUrl = TRNMNT_helpers.parseUrl()
const defaultValues = {
    page: 1,
    team: '',
    player: '',
    gameType: '',
    tournamentType: 'all',
    tournamentSeries: '',
    tournament: '',
    division: '',
    status: schedule ? 'schedule' : 'results',
    // status: 'results',
};
const urlQueryParamStrings = {
    mode: 'gctm',
    page: 'gcp',
    team: 'gct',
    player: 'gcu',
    gameType: 'gcgt',
    tournamentType: 'gctrt',
    tournamentSeries: 'gctrs',
    tournament: 'gctr',
    division: 'gcd',
    status: 'gcs',
};
const isTournamentPage = !!tournamentId

const teams = ref([])
const players = ref([])
const series = ref([])
const tournaments = ref([])
const games = ref([])
const gamesTotal = ref(0)
const gamesMode = ref(mode)

const page = ref(setParamStartValue('page'))
const team = ref(setParamStartValue('team'))
const player = ref(setParamStartValue('player'))
const gameType = ref(setParamStartValue('gameType'))
const tournamentType = ref(setParamStartValue('tournamentType'))
const tournamentSeries = ref(setParamStartValue('tournamentSeries'))
const tournament = ref(tournamentId ? tournamentId : setParamStartValue('tournament'))
const division = ref(setParamStartValue('division'))
const status = ref(setParamStartValue('status'))

const isMoreGamesButtonVisible = ref(false)
const isLessGamesButtonVisible = ref(page.value > 1)

const divisionLetters = computed(() => {
    const divisions = {}
    for (let i = 1; i <= 26; i += 1) {
        divisions[i] = String.fromCharCode(64 + i)
    }
})
const filteredTournaments = computed(() => {
    let data = []
    if (tournamentSeries.value) {
        data = tournaments.value.filter(t => t.series_id === tournamentSeries.value)
    }
    if (tournamentType.value !== defaultValues.tournamentType) {
        data = tournaments.value.filter(t => '' + t.min_players === tournamentType.value)
    }

    return data
})
const filteredPlayers = computed(() => {
    const foundTournament = tournaments.value.find(t => t.id === +tournament.value)

    return foundTournament
        ? players.value.filter(p => foundTournament.players.indexOf(p.id) !== -1)
        : players.value
})
const filteredTeams = computed(() => {
    const foundTournament = tournaments.value.find(t => t.id === +tournament.value)

    return foundTournament
        ? teams.value.filter(t => foundTournament.teams.indexOf(t.id) !== -1)
        : teams.value
});
const filteredSeries = computed(() => {
    return tournamentType.value !== defaultValues.tournamentType
        ? series.value.filter(s => s.id[s.id.length - 1] === tournamentType.value)
        : series.value
});
const filteredDivisions = Vue.computed(() => {
    const foundTournament = tournaments.value.find(t => t.id === +tournament.value);

    return foundTournament
        ? foundTournament.divisions.sort(function (a, b) {
            if (a > b) return 1;
            if (a == b) return 0;
            if (a < b) return -1;
        })
        : [];
});

onMounted(() => {
    gamesMode.value = currentUrl.params && currentUrl.params.gctm ? currentUrl.params.gctm : mode

    if (gamesMode.value === 'group') {
        readTeams();
    } else {
        readPlayers();
    }
    readTournaments();
    readGames();
})

function onChangeMode() {
    console.log(`[GAME CENTER] Change mode to ${gamesMode.value}`)

    if (gamesMode.value === 'group') {
        !teams.value.length && readTeams()
    } else {
        !players.value.length && readPlayers()
    }
    readTournaments()

    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.mode, gamesMode.value)
    setDefaultValue(page, 'page')
    setDefaultValue(team, 'team')
    setDefaultValue(player, 'player')
    setDefaultValue(gameType, 'gameType')
    setDefaultValue(tournamentType, 'tournamentType')
    setDefaultValue(tournamentSeries, 'tournamentSeries')
    setDefaultValue(tournament, 'tournament')
    setDefaultValue(division, 'division')
    setDefaultValue(status, 'status')

    readGames()
}

function onClickLessGames() {
    console.log(`[GAME CENTER] Load less games`)
    if (page.value > 1) {
        page.value -= 1

        TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.page, page.value)
        readGames(false, true)
    }
}

function onClickMoreGames() {
    console.log(`[GAME CENTER] Load more games`)
    page.value += 1

    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.page, page.value);

    readGames(false)
}

function onChangeTeam() {
    console.log(`[GAME CENTER] Change team to ${team.value}`)

    if (team.value) {
        setDefaultValue(division, 'division')
    }
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.team, team.value)
    setDefaultValue(page, 'page')

    readGames()
}

function onChangePlayer() {
    console.log(`[GAME CENTER] Change player to ${player.value}`)
    if (player.value) {
        setDefaultValue(division, 'division')
    }
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.player, player.value)
    setDefaultValue(page, 'page')

    readGames()
}

function onChangeGameType() {
    console.log(`[GAME CENTER] Change game type to ${gameType.value}`)
    if (gameType.value !== 'regular' || !tournament.value) {
        setDefaultValue(division, 'division')
    }
    TRNMNT_helpers.insertURLQueryParam('gcgt', gameType.value)
    setDefaultValue(page, 'page')

    readGames();
}

function onChangeTournament() {
    console.log(`[GAME CENTER] Change tournament to ${tournament.value}`)
    const foundTournament = tournaments.value.find(t => t.id === tournament.value)
    if (!foundTournament) {
        setDefaultValue(division, 'division')
    }

    if (gameType.value !== 'regular' || !tournament.value) {
        setDefaultValue(division, 'division')
    }
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.tournament, tournament.value)
    setDefaultValue(page, 'page')

    readGames();
}

function onChangeTournamentType() {
    console.log(`[GAME CENTER] Change tournament type to ${tournamentType.value}`)
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.tournamentType, tournamentType.value)
    setDefaultValue(tournament, 'tournament')
    setDefaultValue(division, 'division')
    setDefaultValue(page, 'page')

    readGames()
}

function onChangeTournamentSeries() {
    console.log(`[GAME CENTER] Change tournament series to ${tournamentSeries.value}`)
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.tournamentSeries, tournamentSeries.value)
    setDefaultValue(tournament, 'tournament')
    setDefaultValue(division, 'division')
    setDefaultValue(page, 'page')

    readGames()
}

function onChangeDivision() {
    console.log(`[GAME CENTER] Change division to ${division.value}`)
    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.division, division.value)
    setDefaultValue(page, 'page')

    readGames()
}

function onChangeStatus(value) {
    console.log(`[GAME CENTER] Change status to ${value}`)
    status.value = value

    TRNMNT_helpers.insertURLQueryParam(urlQueryParamStrings.status, status.value)
    setDefaultValue(page, 'page')

    readGames()
}

function readTeams() {
    fetch(TEAMS_LIST_URL)
        .then(response => response.json())
        .then(data => {
            teams.value = data.data.teams
        });
}

function readPlayers() {
    fetch(PLAYERS_LIST_URL)
        .then(response => response.json())
        .then(data => {
            players.value = data.data.players
        })
}

function readTournaments() {
    fetch(gamesMode.value === 'group' ? TEAMS_TOURNAMENTS_LIST_URL : PLAYERS_TOURNAMENTS_LIST_URL)
        .then(response => response.json())
        .then(data => {
            series.value = data.data.series
            tournaments.value = data.data.tournaments
        })
}

function readGames(reset = true, prepend = false) {
    if (reset) {
        games.value = []
        gamesTotal.value = 0
        if (page.value === 1) {
            isLessGamesButtonVisible.value = false
        }
        isMoreGamesButtonVisible.value = false
    }

    console.log(`[GAME CENTER] gameType.value = ${gameType.value}`)

    const url = gamesMode.value === 'group' ? GROUP_GAMES_URL : VERSUS_GAMES_URL
    const searchParams = {
        page: page.value,
    };
    if (team.value) {
        searchParams.team_id = team.value
    }
    if (player.value) {
        searchParams.user_id = player.value
    }
    if (gameType.value) {
        searchParams.game_type = gameType.value
    }
    if (tournament.value) {
        searchParams.tournament_id = tournament.value
    }
    if (tournamentType.value) {
        searchParams.tournament_type = tournamentType.value
    }
    if (tournamentSeries.value) {
        searchParams.tournament_series = tournamentSeries.value
    }
    if (division.value) {
        searchParams.division = division.value;
    }
    if (['schedule', 'results'].indexOf(status.value) !== -1) {
        searchParams.status = status.value
    }
    if (limit) {
        searchParams.limit = limit
    }

    fetch(url + '?' + new URLSearchParams(searchParams).toString())
        .then(response => response.json())
        .then(data => {
            if (prepend) {
                games.value = data.data.concat(games.value)
            } else {
                games.value = games.value.concat(data.data)
            }
            gamesTotal.value = data.recordsTotal;
            isMoreGamesButtonVisible.value = games.value.length < data.recordsTotal
        });
}

function setDefaultValue(param, paramKey) {
    console.log(`[GAME CENTER] Set '${paramKey}' to default`)

    param.value = defaultValues[paramKey];
    TRNMNT_helpers.deleteQueryParam(urlQueryParamStrings[paramKey])
}

function setParamStartValue(param, numeric = false) {
    return currentUrl.params && currentUrl.params[urlQueryParamStrings[param]]
        ? (numeric ? +currentUrl.params[urlQueryParamStrings[param]] : currentUrl.params[urlQueryParamStrings[param]])
        : defaultValues[param]
}
</script>

<template>
<template v-if="showFilters">
    <!-- {{--Переключатель режима--}} -->
    <div v-if="showModeSwitch" class="w-100 d-flex justify-content-center mb-5">
        <div class="btn-group" role="group" aria-label="Tournament type">
            <input type="radio"
                class="btn-check"
                name="tournamentMode"
                id="tournamentModeGroup"
                autocomplete="off"
                value="group"
                v-model="gamesMode"
                @change="onChangeMode"
            >
            <label class="btn btn-outline-primary fs-3 w-50" for="tournamentModeGroup">
                <img src="/images/teamplay.svg" alt="Teamplay" height="50">
            </label>

            <input type="radio"
                class="btn-check"
                name="tournamentMode"
                id="tournamentModeVersus"
                autocomplete="off"
                value="versus"
                v-model="gamesMode"
                @change="onChangeMode"
            >
            <label class="btn btn-outline-primary fs-3 w-50" for="tournamentModeVersus">
                <img src="/images/solo.svg" alt="Solo" height="50">
            </label>
        </div>
    </div>
    <!-- {{--Конец переключателя режима--}} -->

    <div class="row">
        <div
            v-if="showSelects"
            class="col-12 col-xxl-8 mb-2 mb-xxl-0 d-flex flex-column flex-xxl-row"
        >
            <div v-if="gamesMode === 'group'" class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangeTeam" v-model="team" class="form-select form-control-dark match-filter-team">
                    <option value="">Все команды</option>
                    <template v-for="team in filteredTeams">
                        <option :value="team.id">{{ team.title }}</option>
                    </template>
                </select>
            </div>
            <div v-if="gamesMode === 'versus'" class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangePlayer" v-model="player" class="form-select form-control-dark match-filter-player">
                    <option value="">Все игроки</option>
                    <template v-for="player in filteredPlayers">
                        <option :value="player.id">{{ player.tag.tag }}</option>
                    </template>
                </select>
            </div>
            <div class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangeGameType" v-model="gameType" class="form-select form-control-dark match-filter-game_type">
                    <option value="">Все игры</option>
                    <option value="playoff">Плей-офф</option>
                    <option value="regular">Регулярный чемпионат</option>
                </select>
            </div>
            <div v-if="!isTournamentPage && gamesMode === 'group'" class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangeTournamentType" v-model="tournamentType" class="form-select form-control-dark match-filter-tournament-type">
                    <option value="all">Все режимы</option>
                    <option value="6">6x6</option>
                    <option value="3">3x3</option>
                </select>
            </div>
            <div v-if="!isTournamentPage" class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangeTournamentSeries" v-model="tournamentSeries" class="form-select form-control-dark match-filter-tournament-type">
                    <option value="">Все серии</option>
                    <option v-for="item in filteredSeries" :value="item.id">{{ item.title }}</option>
                </select>
            </div>
            <div v-if="!isTournamentPage" class="me-xxl-4 mb-2 mb-xxl-0">
                <select @change="onChangeTournament" v-model="tournament" class="form-select form-control-dark match-filter-tournament">
                    <option value="">Все турниры</option>
                    <option v-for="tournament in filteredTournaments" :value="tournament.id">{{ tournament.title }}</option>
                </select>
            </div>
            <div
                v-if="filteredDivisions.length > 1 && gameType === 'regular' && !team && !player"
                class="me-xxl-4 mb-2 mb-xxl-0"
            >
                <select @change="onChangeDivision" v-model="division" class="form-select form-control-dark match-filter-division">
                    <option value="">Группа</option>
                    <option v-for="division in filteredDivisions" :value="division">
                        Группа {{ divisionLetters[division] }}
                    </option>
                </select>
            </div>
        </div>

        <ul
            :class="{'nav nav-tabs justify-content-start justify-content-lg-end mb-4 mb-xxl-0 fs-7 fs-lg-4 fw-bold col-12 col-xxl-4': true, 'w-100': !showSelects}"
            id="gameStatusFilter"
            role="tablist"
        >
            <li class="nav-item" role="presentation">
                <button class="nav-link"
                        :class="{ active: status === 'schedule' }"
                        id="schedule-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#schedule-tab-pane"
                        type="button"
                        role="tab"
                        aria-controls="schedule-tab-pane"
                        :aria-selected="status === 'schedule'"
                        @click.prevent="onChangeStatus('schedule')"
                >
                    Расписание
                </button>
            </li>
            <li class="nav-item" role="presentation">
                <button class="nav-link"
                        :class="{ active: status === 'results' }"
                        id="results-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#results-tab-pane"
                        type="button"
                        role="tab"
                        aria-controls="results-tab-pane"
                        :aria-selected="status === 'results'"
                        @click.prevent="onChangeStatus('results')"
                >
                    Результаты
                </button>
            </li>
        </ul>
    </div>
</template>

<div v-if="games.length" class="match-top">
    <div class="match-top__item display20 bold-text gray-text">Гости</div>
    <div class="match-top__item display20 bold-text gray-text">Хозяева</div>
</div>

<div v-if="showFilters && isLessGamesButtonVisible" class="detail-stat__nav">
    <a @click.stop.prevent="onClickLessGames" class="_all-players detail-stat__btn btn btn-outline-gray">
        Ещё матчи
    </a>
</div>

<div v-if="games.length" class="match-items">
    <game-center-game v-for="game in games" :key="game.id" :game="game"/>
</div>

<div v-if="showFilters && isMoreGamesButtonVisible" class="detail-stat__nav">
    <a @click.stop.prevent="onClickMoreGames" class="_all-players detail-stat__btn btn btn-outline-gray">
        Ещё матчи
    </a>
</div>

<h4 v-if="!games.length" class="match-filter-no_games text-center">
    Ни одной игры не найдено
</h4>
</template>
